import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BarChart3, RefreshCcw, Sparkles, Check, X, Minus } from "lucide-react";
import { toast } from "sonner";
import { MathText } from "@/components/MathText";

export default function ExamResult() {
    const { sessionId } = useParams();
    const nav = useNavigate();
    const [result, setResult] = useState(null);
    const [questions, setQuestions] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/exam/session/${sessionId}/result`);
                setResult(r.data);
                const s = await api.get(`/exam/session/${sessionId}`);
                const map = {};
                s.data.questions.forEach((q) => { map[q.id] = q; });
                setQuestions(map);
            } catch (e) { toast.error("Result not found"); nav("/dashboard"); }
        })();
    }, [sessionId, nav]);

    if (!result) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground font-mono">Loading result…</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <span className="overline text-primary">▮ Scorecard</span>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                {result.exam} mock · <span className="font-mono">{result.total_marks}</span>
                <span className="text-muted-foreground text-2xl">/{result.max_marks}</span>
            </h1>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
                <BigStat label="Score %" value={`${result.percentage}%`} accent />
                <BigStat label="Accuracy" value={`${result.accuracy}%`} />
                <BigStat label="Correct / Wrong" value={`${result.correct} / ${result.incorrect}`} />
                <BigStat label="Unanswered" value={result.unanswered} />
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-sm p-6">
                    <h2 className="font-heading font-bold text-xl">Subject breakdown</h2>
                    <div className="mt-6 space-y-4">
                        {Object.entries(result.subject_breakdown).map(([subj, b]) => {
                            const pct = b.total > 0 ? (b.correct / b.total) * 100 : 0;
                            return (
                                <div key={subj} data-testid={`subject-breakdown-${subj}`}>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-medium">{subj}</span>
                                        <span className="font-mono">{b.correct}/{b.total} · <span className="text-primary">{b.marks.toFixed(1)}</span></span>
                                    </div>
                                    <Progress value={pct} className="h-1 mt-2 rounded-sm" />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="border border-primary rounded-sm p-6 bg-primary/5">
                        <Sparkles className="w-6 h-6 text-primary" />
                        <h3 className="mt-3 font-heading font-bold text-lg">AI diagnostic</h3>
                        <p className="text-xs text-muted-foreground mt-1">Get a 7-day plan, weakness heatmap, and readiness score.</p>
                        <Link to={`/analytics/${sessionId}`}>
                            <Button data-testid="result-view-analytics-btn" className="w-full mt-4 rounded-sm bg-primary hover:bg-primary/90 btn-press">
                                <BarChart3 className="w-4 h-4 mr-2" /> View AI analytics
                            </Button>
                        </Link>
                    </div>
                    <Link to="/generate">
                        <Button variant="outline" data-testid="result-retry-btn" className="w-full rounded-sm btn-press">
                            <RefreshCcw className="w-4 h-4 mr-2" /> Generate another set
                        </Button>
                    </Link>
                </div>
            </div>

            <section className="mt-12">
                <h2 className="font-heading font-bold text-2xl">Answer key & explanations</h2>
                <div className="mt-6 space-y-3">
                    {result.results.map((r, i) => {
                        const q = questions[r.question_id];
                        if (!q) return null;
                        const Icon = r.is_correct ? Check : r.selected_option ? X : Minus;
                        const color = r.is_correct ? "text-green-600" : r.selected_option ? "text-primary" : "text-muted-foreground";
                        return (
                            <details key={r.question_id} className="border border-border rounded-sm">
                                <summary className="cursor-pointer p-4 flex items-start gap-3 hover:bg-accent/40 rounded-sm list-none">
                                    <span className={`w-8 h-8 rounded-sm border border-border flex items-center justify-center ${color} font-mono font-bold flex-shrink-0`}>
                                        <Icon className="w-4 h-4" />
                                    </span>
                                    <div className="flex-1">
                                        <div className="text-xs font-mono text-muted-foreground">Q{i + 1} · {q.subject} · {q.topic}</div>
                                        <MathText inline className="text-sm mt-1 line-clamp-2">{q.question}</MathText>
                                    </div>
                                    <span className="font-mono text-xs font-bold">
                                        Ans: <span className="text-primary">{r.correct_option}</span>
                                    </span>
                                </summary>
                                <div className="p-4 border-t border-border">
                                    <MathText className="text-sm mb-3">{q.question}</MathText>
                                    <div className="grid sm:grid-cols-2 gap-2 mb-4">
                                        {q.options.map((o) => (
                                            <div key={o.key} className={`p-2 border rounded-sm text-sm ${o.key === r.correct_option ? "border-green-600 bg-green-500/10" : r.selected_option === o.key ? "border-primary bg-primary/10" : "border-border"}`}>
                                                <span className="font-mono font-bold mr-2">{o.key}</span><MathText inline>{o.text}</MathText>
                                            </div>
                                        ))}
                                    </div>
                                    {q.explanation && (
                                        <div className="text-sm bg-muted/50 border border-border rounded-sm p-3">
                                            <div className="overline text-muted-foreground mb-1">Explanation</div>
                                            <MathText>{q.explanation}</MathText>
                                        </div>
                                    )}
                                </div>
                            </details>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

const BigStat = ({ label, value, accent }) => (
    <div className="border border-border rounded-sm p-6">
        <div className="overline text-muted-foreground">{label}</div>
        <div className={`font-heading font-black text-4xl mt-2 font-mono ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
);
